import React from 'react';
import '../../../style/transformerTariffs.scss';
import { IRootState } from '../../../@types/redux';
import TariffHelper, { ISPUTariff } from '../../../@types/model/transformer/tariffs/tariffs';
import firebaseApp from '../../../services/firebaseService';
import generalFunctions from '../../../store/general/functions';
import firebase from 'firebase/app';
import { Formik, FieldArray, FormikProps } from 'formik';
import OutlinedStadiumButton from '../../customComponents/button/OutlinedStadiumButton';
import StadiumButton from '../../customComponents/button/StadiumButton';
import AbsoluteLinearProgress from '../../customComponents/progress/AbsoluteLinearProgress';
import Paper from '@material-ui/core/Paper';
import { useSelector } from 'react-redux';
import DateMillisInput from '../../customComponents/form/DateMillisInput';
import LandrateInput from './components/LandrateInput';

interface ISPUEditProps {
    spu : ISPUTariff;
    onSaved : () => void;

    enabled : boolean;
    disabledStartDate ?: boolean;
}

const SPUEdit = (props : ISPUEditProps) => {
    const auth = useSelector((state : IRootState) => state.auth);

    const save = async (values : ISPUTariff) => {
        if (!auth.session) return;
        const spuToSave = { ...values };

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!spuToSave.ref) {
            spuToSave.ref = TariffHelper.spuDoc();
            spuToSave.createdBy = firebaseApp.firestore().collection('employee').doc(auth.session.firebaseUser.uid);
            spuToSave.createdByEmployee = auth.session.employee.EmployeeNumber ?? '';
            spuToSave.createdByName = auth.session.employee.Name;
            spuToSave.createdOn = firebase.firestore.Timestamp.now();
        }

        spuToSave.updatedBy = firebaseApp.firestore().collection('employee').doc(auth.session.firebaseUser.uid);
        spuToSave.updatedByEmployee = auth.session.employee.EmployeeNumber ?? '';
        spuToSave.updatedByName = auth.session.employee.Name;
        spuToSave.updatedOn = firebase.firestore.Timestamp.now();

        try {
            await TariffHelper.saveSPU(spuToSave);
            props.onSaved();
        } catch (ex) {
            generalFunctions.generalShowError(ex, 'Error saving.');
            throw ex;
        }
    };

    const getRowName = (key : string) : string => {
        if (key === '5') {
            return 'Landlight 20A';
        } else if (key === '6') {
            return 'Landlight 60A';
        } else if (key === 'DX') {
            return 'Landrate DX*';
        } else {
            return `Landrate ${key}`;
        }
    };

    return (
        <div className='fdc flx1 ais p30 transformer-tariffs spu'>
            <Formik
                enableReinitialize
                initialValues={props.spu}
                onSubmit={save}
            >
                {({
                    values,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    handleReset,
                    isSubmitting,
                } : FormikProps<ISPUTariff>) => (
                    <form id='spu-form' className='' onSubmit={handleSubmit} onReset={handleReset}>
                        <div className='fdr pb10'>
                            <DateMillisInput name='startDate' label='Start Date' className='' disabled={isSubmitting || !props.enabled || (props.disabledStartDate ?? false)} />
                            {
                                !!values.endDate &&
                                <DateMillisInput name='endDate' label='End Date' className='ml20' disabled />
                            }
                        </div>
                        <div className='fdr jcfe wfill mb5 fsi'>
                            <span>Note: All tariffs should be entered exclusive of VAT.</span>
                        </div>
                        <Paper className='fdc ais' elevation={3}>
                            <div className='fdr ais'>
                                <div className='fdc ais jcc flx1'/>
                                <div className='fdc flx1 ais pt10 pb10 energy-charge'>
                                    <span className='fdr flx1 jcc aic tac'>
                                        Energy Charge [c/kWh]
                                    </span>
                                </div>
                                <div className='fdc flx1 ais pt10 pb10 ancillary-service'>
                                    <span className='fdr flx1 jcc aic tac'>
                                        Ancillary Service [c/kWh]
                                    </span>
                                </div>
                                <div className='fdc flx1 ais pt10 pb10 network-demand'>
                                    <span className='fdr flx1 jcc aic tac'>
                                        Network Demand Charge [c/kWh]
                                    </span>
                                </div>
                                <div className='fdc flx1 ais pt10 pb10 network-capacity'>
                                    <span className='fdr flx1 aic jcc tac'>
                                        Network Capacity Charge [R/POD/day]
                                    </span>
                                </div>
                                <div className='fdc flx1 ais pt10 pb10 service-charge'>
                                    <span className='fdr flx1 aic jcc tac'>
                                        Service and Administration Charge [R/POD/Day]
                                    </span>
                                </div>
                                <div className='fdc flx1 ais pt10 pb10 generation-capacity-charge'>
                                    <span className='fdr flx1 aic jcc tac'>
                                        Generation Capacity Charge [R/POD/Day]
                                    </span>
                                </div>
                            </div>
                            {
                                isSubmitting &&
                                <div className='fdc ais wfill submitting'>
                                    <AbsoluteLinearProgress />
                                </div>
                            }
                            <div className='fdc ais flx2'>
                                <FieldArray
                                    name='landrates'
                                    render={
                                        () => (
                                            <div className='fdc ais flx1'>
                                                {
                                                    Object.keys(values.landrates).map(key => {
                                                        const landrate = values.landrates[key];
                                                        const isDisabled = isSubmitting || !props.enabled;
                                                        return (
                                                            <div className='fdr tariff-row mnh65' key={`landrate_${key}`}>
                                                                <div className='fdc aic jcc flx1 landrate'>
                                                                    {getRowName(key)}
                                                                </div>
                                                                <div className='fdc ais jcc flx1 br'>
                                                                    <LandrateInput
                                                                        value={landrate.energyCharge}
                                                                        name={`landrates.${key}.energyCharge`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                <div className='fdc ais jcc flx1 br'>
                                                                    <LandrateInput
                                                                        value={landrate.ancillaryService}
                                                                        name={`landrates.${key}.ancillaryService`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                <div className='fdc ais jcc flx1 br'>
                                                                    <LandrateInput
                                                                        value={landrate.networkDemand}
                                                                        name={`landrates.${key}.networkDemand`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                <div className='fdc ais jcc flx1 br'>
                                                                    <LandrateInput
                                                                        value={landrate.networkCapacity}
                                                                        name={`landrates.${key}.networkCapacity`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                        currencySymbol='R'
                                                                    />
                                                                </div>
                                                                <div className='fdc ais jcc flx1'>
                                                                    <LandrateInput
                                                                        value={landrate.serviceCharge}
                                                                        name={`landrates.${key}.serviceCharge`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                        currencySymbol='R'
                                                                    />
                                                                </div>
                                                                <div className='fdc ais jcc flx1 br'>
                                                                    <LandrateInput
                                                                        value={landrate.generationCapacityCharge}
                                                                        name={`landrates.${key}.generationCapacityCharge`}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        disabled={isDisabled}
                                                                        currencySymbol='R'
                                                                    />
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        )
                                    }
                                />
                                <div className='fdr aic pl10 mnh65 disclaimer'>
                                    <span>* R/day fixed charge inclusive of the following charges; energy, ancillary service, network demand, network capacity and service charge.</span>
                                </div>
                            </div>
                        </Paper>
                        <div className='fdr flx1 jcfe aife pt10'>
                            <OutlinedStadiumButton className='mr16' variant='text' type='reset' disabled={isSubmitting || !props.enabled}>
                                    CANCEL
                            </OutlinedStadiumButton>
                            <StadiumButton type='submit' disabled={isSubmitting || !props.enabled}>
                                    SAVE
                            </StadiumButton>
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    );
};


export default SPUEdit;
